import { useEffect, useRef, useCallback } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { MapStyleManager, MapStyleType } from '../services/MapStyleManager';
import { TrackManager } from '../services/TrackManager';
import config from '../config';

interface UseMapStyleManagerOptions {
  map: MapLibreMap | null;
  trackManager: TrackManager | null;
  is3D: boolean;
}

export const useMapStyleManager = ({
  map,
  trackManager,
  is3D
}: UseMapStyleManagerOptions) => {
  const managerRef = useRef<MapStyleManager>(
    new MapStyleManager(config.map.tileServerUrl)
  );

  // Update map reference
  useEffect(() => {
    if (map) {
      // Setting map in MapStyleManager
      managerRef.current.setMap(map);
    }
  }, [map]);

  // Update track manager reference
  useEffect(() => {
    if (trackManager) {
      // Setting TrackManager in MapStyleManager
      managerRef.current.setTrackManager(trackManager);
    }
  }, [trackManager]);

  // Switch to a new style
  const switchToStyle = useCallback(async (style: MapStyleType) => {
    // Don't switch styles if we're in 3D mode
    if (is3D) {
      // Skipping style switch - in 3D mode
      return;
    }

    if (!map) {
      // No map available for style switching
      return;
    }

    await managerRef.current.switchToStyle(style);
  }, [map, is3D]);

  // Get current style
  const getCurrentStyle = useCallback(() => {
    return managerRef.current.getCurrentStyle();
  }, []);

  // Check if transitioning
  const isTransitioning = useCallback(() => {
    return managerRef.current.isInTransition();
  }, []);

  return {
    switchToStyle,
    getCurrentStyle,
    isTransitioning
  };
};